/**
 * Shared error helpers for openapi-fetch results. Several endpoints in the
 * generated client don't declare error responses, so the typed `error` is
 * `never`. We still need to handle non-2xx at runtime — Response carries that.
 */

import { DomainError, type ApiError } from '@leanin/shared/errors';

export type FailureResult = { ok: false; code: string; message: string };

export type ApiResult = { ok: true } | FailureResult;

export async function failureFrom(
  response: Response,
  fallback: string,
): Promise<FailureResult> {
  let code = `http_${response.status}`;
  let message = fallback;
  try {
    const body = (await response.clone().json()) as {
      code?: unknown;
      message?: unknown;
    };
    if (typeof body.code === 'string') code = body.code;
    if (typeof body.message === 'string') message = body.message;
  } catch {
    // Body wasn't JSON — keep the fallback message.
  }
  return { ok: false, code, message };
}

export function isOk(response: Response): boolean {
  return response.ok;
}

/**
 * Marker error thrown by `throwApiError` when the underlying response was
 * 404. Detail-page routes test for this via `isNotFoundApiError(err)` and
 * re-throw `notFound()` from `@tanstack/react-router` so the branded
 * 404 page renders instead of the generic "Couldn't load …" surface.
 */
export class NotFoundApiError extends DomainError {
  readonly status = 404;
  constructor(code: string) {
    super({ code } as ApiError);
    Object.setPrototypeOf(this, NotFoundApiError.prototype);
    this.name = 'NotFoundApiError';
  }
}

export function isNotFoundApiError(err: unknown): err is NotFoundApiError {
  return err instanceof NotFoundApiError;
}

/**
 * Single typed entry point for narrowing openapi-fetch `error` payloads to
 * the server's `code` string. The wire shape is `{ error: { code, message,
 * details? } }`; centralising the assertion here means an envelope-shape
 * evolution only touches this module.
 */
export function parseApiErrorCode(error: unknown): string | null {
  const inner = (error as { error?: { code?: unknown } } | undefined)?.error;
  if (inner === null || inner === undefined) return null;
  return typeof inner.code === 'string' ? inner.code : null;
}

/**
 * Companion to `parseApiErrorCode` for routes that need the validation
 * `details` envelope (per-field arrays). Returns `null` when the field is
 * absent or shape-mismatched.
 */
export function parseApiErrorDetails(
  error: unknown,
): Record<string, ReadonlyArray<string>> | null {
  const inner = (error as { error?: { details?: unknown } } | undefined)?.error;
  if (inner === null || inner === undefined) return null;
  const details = inner.details;
  if (details === null || typeof details !== 'object') return null;
  return details as Record<string, ReadonlyArray<string>>;
}

/**
 * Companion to `parseApiErrorCode` for surfaces that need the server's
 * human-readable message.
 */
export function parseApiErrorMessage(error: unknown): string | null {
  const inner = (error as { error?: { message?: unknown } } | undefined)?.error;
  if (inner === null || inner === undefined) return null;
  return typeof inner.message === 'string' ? inner.message : null;
}

/**
 * Re-throws an openapi-fetch `error` value as a `DomainError`, preserving the
 * server's `code` from the body envelope. Use at api-client mutation sites
 * where the existing action layer expects to catch a `DomainError` and run it
 * through `failureFromError()`.
 *
 * Pass `response` so a 404 becomes a typed `NotFoundApiError`; detail-page
 * routes (`/members/{id}`, `/circles/{id}`, …) check for this and render
 * the branded NotFoundPage instead of a generic retry surface.
 */
export function throwApiError(error: unknown, response?: Response): never {
  const code = parseApiErrorCode(error) ?? 'http_error';
  if (response?.status === 404) {
    throw new NotFoundApiError(code);
  }
  throw new DomainError({ code } as ApiError);
}

/**
 * Convert a thrown error (typically a `DomainError`) into the same
 * `{ ok: false, code, message }` shape `failureFrom` produces for HTTP
 * responses. Used by server actions that call services in-process: the
 * service throws on failure, the action translates to the wire shape.
 */
export function failureFromError(err: unknown, fallback: string): FailureResult {
  // Wire envelope first — `parseApiErrorCode`/`Message` is the canonical
  // narrowing surface (it owns the `as` cast). Fall through to the
  // top-level `{ code, message }` shape that thrown `DomainError`s
  // produce in-process.
  const envelopeCode = parseApiErrorCode(err);
  if (envelopeCode !== null) {
    return {
      ok: false,
      code: envelopeCode,
      message: parseApiErrorMessage(err) ?? fallback,
    };
  }
  if (err !== null && typeof err === 'object') {
    const e = err as { code?: unknown; message?: unknown };
    const code = typeof e.code === 'string' ? e.code : 'UNKNOWN';
    const message = typeof e.message === 'string' ? e.message : fallback;
    return { ok: false, code, message };
  }
  return { ok: false, code: 'UNKNOWN', message: fallback };
}
